import React, { useEffect, useState } from "react";
import { SweetAlertShow, LoadingDiv, formatDateTime } from "../master/Master";
import {
  distributionKitAssignmentList,
  kitAssignmentRemove,
} from "../../api/DistributionKit";
import Swal from "sweetalert2";

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "student", label: "Students" },
  { value: "batch", label: "Batches" },
  { value: "staff", label: "Staff" },
];

// assignable_type comes back as the full FQCN (e.g. "App\Models\Student") —
// take the class basename for a friendly label.
const typeLabel = (assignableType) => {
  if (!assignableType) return "-";
  const parts = assignableType.split("\\");
  return parts[parts.length - 1];
};

const assignedName = (row) => {
  const a = row.assignable;
  if (!a) return "Unknown (deleted?)";

  return (
    a.name ||
    a.title ||
    a.student_name ||
    a.staff_name ||
    `#${row.assignable_id}`
  );
};

/**
 * Props:
 *  - kit: { id, name }               (required)
 *  - item: { id, name }              (optional — scopes the list to just
 *           this item's assignments instead of the whole kit)
 *  - onClose: () => void
 */
export default function KitAssignmentsModal({ kit, item, onClose }) {
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");

  const getAssignments = async () => {
    setLoading(true);

    try {
      let addUrl = `per_page=15&page=${page}`;

      if (item) {
        addUrl += `&item_id=${item.id}`;
      }

      if (typeFilter) {
        addUrl += `&assignable_type=${typeFilter}`;
      }

      const response = await distributionKitAssignmentList(kit.id, addUrl);

      if (response.data.success) {
        const payload = response.data.data;

        setAssignments(payload?.data || []);
        setMeta({
          current_page: payload?.current_page || 1,
          last_page: payload?.last_page || 1,
          total: payload?.total ?? (payload?.data || []).length,
        });
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, typeFilter]);

  const handleRemove = async (assignment) => {
    const result = await Swal.fire({
      title: "Remove Assignment?",
      text: `Are you sure you want to remove the assignment for "${assignedName(
        assignment,
      )}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      const response = await kitAssignmentRemove(assignment.id);

      if (response.data.success) {
        SweetAlertShow(
          "success",
          "Successfully",
          response.data.message || "Assignment removed successfully.",
        );

        getAssignments();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response.data.message || "Unable to remove assignment.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content border-0 shadow-lg">
            {/* HEADER */}
            <div className="modal-header border-bottom px-4 py-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                  style={{ width: 44, height: 44 }}
                >
                  <i className="ri-team-line fs-5 text-primary" />
                </div>

                <div>
                  <h5 className="modal-title fw-bold mb-0">
                    Assignments — {item ? item.name : kit.name}
                  </h5>
                  <small className="text-muted">
                    {meta.total} total assignment{meta.total !== 1 ? "s" : ""}
                  </small>
                </div>
              </div>

              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            {/* BODY */}
            <div className="modal-body p-4 position-relative">
              {loading && <LoadingDiv />}

              <div className="d-flex justify-content-end mb-3">
                <select
                  className="form-select"
                  style={{ maxWidth: 200 }}
                  value={typeFilter}
                  onChange={(e) => {
                    setPage(1);
                    setTypeFilter(e.target.value);
                  }}
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Assigned To</th>
                      <th>Type</th>
                      {!item && <th>Assigned For</th>}
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                      <th>Assigned On</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {!loading && assignments.length === 0 && (
                      <tr>
                        <td colSpan={item ? 8 : 9}>
                          <div className="text-center py-5">
                            <i className="ri-user-line fs-1 text-muted d-block mb-2" />
                            <span className="text-muted">
                              No assignments found
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}

                    {assignments.map((row) => (
                      <tr key={row.id}>
                        <td className="fw-semibold">{assignedName(row)}</td>

                        <td>
                          <span className="badge bg-label-secondary">
                            {typeLabel(row.assignable_type)}
                          </span>
                        </td>

                        {!item && (
                          <td>
                            {row.distribution_kit_item_id
                              ? row.item?.name ||
                                `Item #${row.distribution_kit_item_id}`
                              : "Whole Kit"}
                          </td>
                        )}

                        <td>{row.quantity}</td>

                        <td>₹{row.kit_price}</td>

                        <td className="fw-semibold">₹{row.total_price}</td>

                        <td>
                          {row.kit_price_purchased_at
                            ? formatDateTime(row.kit_price_purchased_at)
                            : "-"}
                        </td>

                        <td>
                          {Number(row.status) === 1 ? (
                            <span className="badge bg-label-success">
                              Active
                            </span>
                          ) : Number(row.status) === 3 ? (
                            <span className="badge bg-label-secondary">
                              Removed
                            </span>
                          ) : (
                            <span className="badge bg-label-secondary">
                              Inactive
                            </span>
                          )}
                        </td>

                        <td className="text-end">
                          {Number(row.status) === 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-label-danger"
                              onClick={() => handleRemove(row)}
                              title="Remove Assignment"
                            >
                              <i className="ri-close-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {meta.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    Page {meta.current_page} of {meta.last_page}
                  </span>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-label-secondary"
                      disabled={meta.current_page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-label-secondary"
                      disabled={meta.current_page >= meta.last_page}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="modal-footer border-top px-4 py-3">
              <button
                type="button"
                className="btn btn-label-secondary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" />
    </>
  );
}
